import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Crown, Infinity, Ticket, Sparkles, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import { userAccessAPI } from "../lib/api";

export default function AccessStatusBadge() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await userAccessAPI.getStatus();
      setStatus(res.data);
    } catch (error) {
      console.error("Error loading access status:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Badge variant="outline" className="animate-pulse">
        <Ticket className="w-3 h-3 mr-1" />
        ...
      </Badge>
    );
  }

  if (!status) return null;

  // Premium users
  if (status.is_premium) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white hover:from-amber-600 hover:to-yellow-600 cursor-default gap-1">
              <Crown className="w-3 h-3" />
              Premium
              <Infinity className="w-3 h-3 ml-1" />
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium">Compte Premium</p>
            <p className="text-sm text-muted-foreground">Tirages et sondages illimités</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Subscription users
  if (status.has_subscription) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 cursor-default gap-1">
              <Sparkles className="w-3 h-3" />
              {status.subscription_type === "premium" ? "Premium" : status.subscription_type === "annual" ? "Annuel" : "Mensuel"}
              <Infinity className="w-3 h-3 ml-1" />
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium">Abonnement {status.subscription_type}</p>
            <p className="text-sm text-muted-foreground">
              Expire le {new Date(status.subscription_end).toLocaleDateString('fr-FR')}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Users with free operations credits
  if (status.free_operations > 0) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link to="/billing">
              <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 cursor-pointer gap-1">
                <Ticket className="w-3 h-3" />
                {status.free_operations} crédit{status.free_operations > 1 ? 's' : ''}
              </Badge>
            </Link>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium">Crédits disponibles</p>
            <p className="text-sm text-muted-foreground">
              {status.free_operations} opération{status.free_operations > 1 ? 's' : ''} gratuite{status.free_operations > 1 ? 's' : ''}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Cliquez pour voir les options</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Free tier (first free draw/poll available)
  if (status.free_draw_available || status.free_poll_available) {
    const freeCount = (status.free_draw_available ? 1 : 0) + (status.free_poll_available ? 1 : 0);
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link to="/billing">
              <Badge variant="outline" className="border-primary text-primary hover:bg-primary/10 cursor-pointer gap-1">
                <Ticket className="w-3 h-3" />
                {freeCount} essai{freeCount > 1 ? 's' : ''} gratuit{freeCount > 1 ? 's' : ''}
              </Badge>
            </Link>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium">Essais gratuits</p>
            {status.free_draw_available && (
              <p className="text-sm text-muted-foreground">1er tirage gratuit disponible</p>
            )}
            {status.free_poll_available && (
              <p className="text-sm text-muted-foreground">1er sondage gratuit disponible</p>
            )}
            <p className="text-xs text-muted-foreground mt-1">Cliquez pour acheter plus</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // No credits left
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link to="/billing">
            <Badge variant="outline" className="border-destructive text-destructive hover:bg-destructive/10 cursor-pointer gap-1">
              <CreditCard className="w-3 h-3" />
              0 crédit
            </Badge>
          </Link>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">Aucun crédit disponible</p>
          <p className="text-sm text-muted-foreground">
            Vos essais gratuits sont épuisés.
          </p>
          <p className="text-xs text-muted-foreground mt-1">Cliquez pour acheter des crédits</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
